import {
    ReactNode,
    cloneElement,
    ElementType,
    isValidElement,
    ReactElement,
} from 'react';
import { Menu } from '@headlessui/react';
import { cn } from '../../../utils';

export type MenuItemProps = {
    children?: (JSX.Element & { className?: string }) | string;
    icon?: ReactNode;
    active?: boolean;
    disabled?: boolean;
    trailingIcon?: ReactNode;
    as?: ElementType;
    className?: string;
    dataTestId?: string;
};

function MenuItem({
    children,
    icon = '',
    disabled = false,
    trailingIcon = false,
    as = 'div',
    className,
    dataTestId,
}: MenuItemProps) {
    return (
        <Menu.Item as={as}>
            {({ active }) => {
                const iconClassName = cn(
                    'alli-h-5 alli-w-5 alli-text-gray-700 group-hover:alli-text-blue-gray-700',
                    className,
                    {
                        'alli-text-blue-gray-700': active,
                        'alli-mr-3': !trailingIcon,
                        'alli-text-gray-600': disabled,
                        // 'alli-text-blue-600': isCurrentUrl,
                    },
                );

                const itemClassName = cn(
                    'alli-group alli-flex alli-items-center alli-px-4 alli-py-2 alli-text-sm alli-text-blue-gray-700 hover:alli-bg-gray-100 ',
                    className,
                    {
                        'alli-bg-gray-100 alli-text-blue-gray-700': active,
                        'alli-pointer-events-none alli-bg-gray-300 alli-text-gray-600':
                            disabled,
                        'alli-flex-row-reverse alli-justify-between':
                            trailingIcon,
                    },
                );

                const iconWrap = icon && (
                    <span className={iconClassName}>{icon}</span>
                );

                if (typeof children === 'string') {
                    return (
                        <div data-testid={dataTestId} className={itemClassName}>
                            {iconWrap}
                            {children}
                        </div>
                    );
                } else if (children && isValidElement(children)) {
                    const child = cloneElement(
                        children as ReactElement<
                            MenuItemProps & { ['data-testid']: string }
                        >,
                        {
                            ['data-testid']: dataTestId,
                            active,
                            disabled,
                            trailingIcon,
                            className: cn(
                                itemClassName,
                                children?.props?.className,
                            ),
                            children: (
                                <>
                                    {iconWrap}
                                    {getChildren(children)}
                                </>
                            ),
                        },
                    );
                    return child;
                }

                return children;
            }}
        </Menu.Item>
    );
}

MenuItem.displayName = 'MenuItem';
export default MenuItem;

// This avoids a typescript issue. But I have no idea how to type it.
// Essentially this MenuItem has children, which is likely going to be
// a ReactRouter `<Link>` component, which has its own children which
// we need to access. If we just do `{children}` then the whole `<Link>`
// would be returned, but we are already returning it. So it's like passing
// '<Link>` to itself and you end up with `<a>` inside `<a>`, so this function
// returns the "content" of the link AKA the "children"
function getChildren(children: ReactElement) {
    return children.props.children;
}
